import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { DateInDateModal } from './DateInDateModal'
import { PlanType, CheckInDateType, searchData } from './type'
import { usePlanStore } from '../../../store/planStore'

export const CheckInDate = (props: { plan: PlanType; checkInDate: CheckInDateType }) => {
  const { plan, checkInDate } = props
  const [showModal, setShowModal] = useState<Boolean>(false)
  const ref = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()
  const { selectedPlan, setSelectedPlan, selectedCheckInDate, setSelectedCheckInDate, setValidationErrors, setOpenModalFilter } =
    usePlanStore()
  const serviceTypes = JSON.parse(
    (document.querySelector('[data-service-types]') as HTMLElement)?.dataset?.serviceTypes || '[]',
  ) as string[]

  useEffect(() => {
    let dateView = searchData.start_date
    if (!dateView) {
      const urlParams = new URLSearchParams(window.location.search)
      const monthStrings = urlParams.get('select_month') || '' // "2025/08,2025/06,2025/10"
      const months = monthStrings.split(',')
      if (months.length) {
        const selected = plan.checkInDates.find((date) => {
          const checkInMonthString = dayjs(new Date(date.date)).format('YYYY/MM')
          return months.includes(checkInMonthString)
        })

        if (checkInDate === selected) dateView = checkInDate.date
      }
    }

    if (dateView === checkInDate.date) {
      const parent = ref.current.parentElement
      parent.scrollLeft = ref.current.offsetLeft - parent.offsetLeft
    }
  }, [searchData])

  useEffect(() => {
    if (selectedCheckInDate) {
      const urlParams = new URLSearchParams(window.location.search)
      const hasPreschoolerBirthdays = urlParams.has('preschooler_birthdays')

      if (
        hasPreschoolerBirthdays &&
        selectedCheckInDate.date === checkInDate.date &&
        checkInDate.status === 'OK' &&
        checkInDate.price &&
        selectedPlan?.planId === plan.planId
      ) {
        setShowModal(true)
        setSelectedCheckInDate(null)
      }
    }
  }, [selectedCheckInDate, checkInDate])

  const handleCloseModal = () => {
    setSelectedCheckInDate(null)
    setShowModal(false)
  }

  const displayPrice = (checkInDate: CheckInDateType) => {
    if (!checkInDate.price) {
      return null
    }

    const price = checkInDate.price / 10000
    const displayPriceText = Number.isInteger(price) ? String(price.toFixed(0)) : price.toFixed(1)
    return `${displayPriceText}${t('reservation_confirm.modal_booking.currency_man')}${t(
      'reservation_confirm.modal_booking.currency',
    )}`
  }

  const handleOpenModal = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasPreschoolerBirthdays = urlParams.has('preschooler_birthdays')
    setSelectedCheckInDate(checkInDate)
    setSelectedPlan(plan)

    if (!hasPreschoolerBirthdays && serviceTypes.some((serviceType) => serviceType.includes('保育園留学'))) {
      setOpenModalFilter(true)
      setValidationErrors({
        childBirthdate: true,
      })
      return
    }

    if (checkInDate.status === 'OK' && checkInDate.price) {
      setShowModal(true)
    }
  }

  return (
    <>
      {showModal && <DateInDateModal plan={plan} checkInDate={checkInDate} handleCloseModal={handleCloseModal} />}
      <div
        ref={ref}
        style={{ width: '84px' }}
        className={`bg-white flex flex-col gap-2 items-center justify-between p-3 text-center transition-all duration-300 h-24 flex-shrink-0 rounded-md ${
          checkInDate.status === 'OK' ? 'border-green border cursor-pointer' : 'border border-gray-300 cursor-not-allowed'
        }`}
        onClick={handleOpenModal}
      >
        <div className={`text-xs ${checkInDate.status === 'OK' ? 'text-green' : 'text-gray-400'}`}>
          {dayjs(new Date(checkInDate.date)).format('M/D')}({t('day.formats.short.sunday')})
        </div>
        <div className={checkInDate.status === 'OK' ? 'text-green' : 'text-gray-400'}>
          {checkInDate.status === '-' && <span>-</span>}
          {checkInDate.status === 'OK' && (
            <svg fill="none" height="20" viewBox="0 0 20 20" width="20" xmlns="http://www.w3.org/2000/svg">
              <rect height="18" rx="9" stroke="#189855" strokeWidth="2" width="18" x="1" y="1" />
            </svg>
          )}
          {checkInDate.status === 'NG' && <span className="font-size-15">満枠</span>}
        </div>
        <div
          className={`text-xs whitespace-nowrap truncate w-full ${checkInDate.status === 'OK' ? 'text-green' : 'text-gray-400'}`}
        >
          {displayPrice(checkInDate)}
        </div>
      </div>
    </>
  )
}
